import { Box, Drawer, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { loaderOff, loaderOn, logout } from "../redux/actions";
import { useDispatch } from "react-redux";
import CommonDialog from "../commondialog/confirm";
import { fermerSession, roleCourant, sujetCourant } from "../api/role";
import { PLAN, entreeActive } from "../navigation/plan";
import { chargerAttente, totalAttente, useAttente, viderAttente } from "../navigation/attente";
import { COULEUR_DOMAINE, Domaine } from "../theme/tribeTheme";
import LogoTribe from "./charte/LogoTribe";
import TroisBarres from "./charte/TroisBarres";
import { CarreDomaine } from "./charte/Bloc";

/* Menu latéral : 8 entrées, une par besoin, sans sous-menu déroulant. Chaque
   entrée ouvre une page d'accueil qui liste ses sous-pages. L'entrée active
   est marquée par les trois barres du E de TRIBE ; un compteur signale les
   éléments en attente quand il y en a (navigation/attente.ts). */

export const LARGEUR_MENU = 232;

function EntreeMenuLien({ to, libelle, domaine, actif, compteur }: {
  to: string; libelle: string; domaine: Domaine; actif: boolean; compteur?: number;
}) {
  return (
    <Box
      component={RouterLink}
      to={to}
      aria-current={actif ? "page" : undefined}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        mx: 1.25, mb: 0.5, px: 1.25, py: 0.85,
        borderRadius: "6px", textDecoration: "none",
        color: "var(--c-text-primary)",
        background: actif ? "var(--c-surface)" : "transparent",
        border: actif ? "1.5px solid var(--c-text-primary)" : "1.5px solid transparent",
        "&:hover": { background: actif ? "var(--c-surface)" : "rgba(31,30,27,0.06)" },
        "&:focus-visible": { outline: "2px solid var(--c-text-primary)", outlineOffset: 1 },
      }}
    >
      <Box sx={{ width: 16, display: "flex", justifyContent: "center", flexShrink: 0 }}>
        {actif ? <TroisBarres taille="m" /> : <CarreDomaine couleur={COULEUR_DOMAINE[domaine]} taille={10} />}
      </Box>
      <Typography sx={{ flex: 1, fontFamily: "var(--font-body)", fontSize: 14.5, fontWeight: actif ? 700 : 500, lineHeight: 1.3, color: "inherit" }}>
        {libelle}
      </Typography>
      {compteur != null && compteur > 0 && (
        <Box component="span" aria-label={`${compteur} en attente`} sx={{
          minWidth: 24, height: 20, px: 0.6, borderRadius: "5px",
          background: "var(--c-text-primary)", color: "#fff",
          fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {compteur > 999 ? "999+" : compteur}
        </Box>
      )}
    </Box>
  );
}

export default function NavBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const attente = useAttente();

  // Compteurs relus en changeant de page (au plus toutes les 60 s).
  useEffect(() => { chargerAttente(); }, [location.pathname]);

  const active = entreeActive(location.pathname);

  const compteurs: Partial<Record<Domaine, number>> = {
    traiter: totalAttente(attente),
    quetes: attente.lignes.quetes?.nombre,
    lieux: attente.lignes.lieux?.nombre,
    argent: attente.lignes.retraits?.nombre,
  };

  const handleLogOut = () => {
    dispatch(loaderOn());
    try {
      dispatch(logout());
      fermerSession();
      viderAttente();
      setIsLoggedIn(false);
      navigate("/sign_in");
    } catch {
      dispatch(loaderOff());
    }
  };

  if (!isLoggedIn) return null;

  const role = ({ SUPER_ADMIN: "Super admin", ADMIN: "Admin", VIEWER: "Lecture seule" } as Record<string, string>)[roleCourant() ?? ""] ?? "";

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: LARGEUR_MENU,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: LARGEUR_MENU,
          boxSizing: "border-box",
          background: "var(--c-menu) !important",
          borderRight: "1px solid var(--c-border) !important",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          color: "var(--c-text-primary) !important",
        },
      }}
    >
      {/* Logo officiel : pictogramme atténué + mot TRIBE */}
      <Box component={RouterLink} to="/accueil" aria-label="Tribe, retour à l'accueil" sx={{
        px: 2.5, pt: 2.75, pb: 2.25, display: "block", textDecoration: "none",
        borderBottom: "1px solid var(--c-border)",
      }}>
        <LogoTribe largeurPicto={30} largeurMot={74} />
        <Typography sx={{ mt: 0.75, fontSize: 11.5, color: "var(--c-text-secondary)", fontWeight: 500 }}>
          Back-office
        </Typography>
      </Box>

      {/* Les 8 entrées */}
      <Box component="nav" aria-label="Menu principal" sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 1.5 }}>
        {PLAN.map(e => (
          <EntreeMenuLien
            key={e.cle}
            to={e.chemin}
            libelle={e.libelle}
            domaine={e.cle}
            actif={active?.cle === e.cle}
            compteur={compteurs[e.cle]}
          />
        ))}
      </Box>

      {/* Admin connecté et déconnexion */}
      <Box sx={{ borderTop: "1px solid var(--c-border)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            border: "1.5px solid var(--c-text-primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ fontFamily: "var(--font-display)", fontSize: "12px", fontWeight: 700, lineHeight: 1 }}>
              {(sujetCourant() ?? "A").slice(0, 1).toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {/* Admin connecté, lu dans le jeton (Z1-28). */}
              {sujetCourant() ?? "Administrateur"}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "var(--c-text-secondary)", lineHeight: 1.3 }}>{role}</Typography>
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setOpenDialog(true)}
          sx={{
            display: "flex", alignItems: "center", gap: 1.25, width: "100%",
            px: 2, py: 1.25, cursor: "pointer", border: "none", borderTop: "1px solid var(--c-border)",
            background: "transparent", color: "var(--c-text-primary)", font: "inherit",
            "&:hover": { background: "rgba(31,30,27,0.06)" },
          }}
        >
          <LogoutIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "inherit" }}>Déconnexion</Typography>
        </Box>
      </Box>

      <CommonDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => { handleLogOut(); setOpenDialog(false); }}
        contentText="Êtes-vous sûr de vouloir vous déconnecter ?"
      />
    </Drawer>
  );
}
